import { useCallback, useEffect, useState } from 'react';

import { Button, Form } from 'antd';
import classNames from 'classnames';

import { MODAL_WIDTHS } from '@/shared/lib/constants';
import { BaseModal } from '@/shared/ui/BaseModal';
import { Icon } from '@/shared/ui/Icon';
import { Switch } from '@/shared/ui/Switch';

import {
  type CreateConnectorRequest,
  normalizeConnectorConfigByFields,
  validateConfigWithFieldRules,
} from '@/entities/connector';

import { hasIncompleteRequiredConfig, isConnectorNameEmpty } from '@/features/connectors/lib/connectorFormState';
import type { ResetModalAction } from '@/features/connectors/lib/type';
import {
  useAssetURIFieldsReset,
  useConfigJsonBridge,
  useConnectorConfigDefaults,
  useConnectorForm,
  useCreateConnector,
} from '@/features/connectors/model';
import styles from '@/features/connectors/ui/_internal/connectorForm.module.css';
import { ConfigJsonView } from '@/features/connectors/ui/ConfigJsonView';
import { ConnectorAssetURIFields } from '@/features/connectors/ui/ConnectorAssetURIFields';
import { ConnectorBasicFields } from '@/features/connectors/ui/ConnectorBasicFields';
import { ConnectorConfigFields } from '@/features/connectors/ui/ConnectorConfigFields';
import { ConnectorStep } from '@/features/connectors/ui/ConnectorStep';
import { ResetModal } from '@/features/connectors/ui/ResetModal/ResetModal';

type CreateConnectorModalProps = {
  open: boolean;
  onClose: () => void;
};

const CREATE_CONNECTOR_FORM_ID_PREFIX = 'create-connector-form';
const CONFIG_FIELDS_TITLE = '연결 설정';
const JSON_VIEW_SWITCH_LABEL = 'JSON으로 설정 보기/편집';

const NEXT_BUTTON_LABEL = (
  <div className={styles.nextButton}>
    <span>다음</span>
    <Icon name="next" size={16} />
  </div>
);

const PREV_BUTTON_LABEL = (
  <div className={styles.prevButton}>
    <Icon name="prev" size={16} />
    <span>뒤로</span>
  </div>
);

export const CreateConnectorModal = ({ open, onClose }: CreateConnectorModalProps) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [resetModalAction, setResetModalAction] = useState<ResetModalAction>(null);

  const {
    form,
    selectedType,
    showConfigJson,
    setShowConfigJson,
    connectorTypeOptions,
    schemaFields,
    isSchemaLoading,
    isSchemaError,
  } = useConnectorForm();

  const {
    watchedFormConfig,
    jsonDisplayConfig,
    jsonUnmappedConfig,
    hasJsonError,
    handleConfigChange,
    handlePartialConfigChange,
    handleJsonErrorChange,
    resetJsonBridge,
  } = useConfigJsonBridge({ form, schemaFields, selectedType });

  useConnectorConfigDefaults({ form, fields: schemaFields });

  const {
    isReset: isAssetURIReset,
    hasRequiredFields: hasAssetURIRequiredFields,
    handleResetFields: handleAssetURIResetFields,
  } = useAssetURIFieldsReset(form);

  const { mutate: createConnector, isPending: isCreateConnectorPending } = useCreateConnector();

  const name = Form.useWatch('name', form);

  const isNameEmpty = isConnectorNameEmpty(name);
  const normalizedSchemaConfig = normalizeConnectorConfigByFields(schemaFields, watchedFormConfig);
  const hasIncompleteRequiredSchemaConfig =
    selectedType &&
    !isSchemaLoading &&
    !isSchemaError &&
    hasIncompleteRequiredConfig(schemaFields, normalizedSchemaConfig);
  const hasInvalidSchemaConfig =
    !!selectedType &&
    !isSchemaLoading &&
    !isSchemaError &&
    !validateConfigWithFieldRules(normalizedSchemaConfig, schemaFields).ok;

  const isSubmitDisabled =
    isNameEmpty ||
    !selectedType ||
    isSchemaLoading ||
    isSchemaError ||
    isCreateConnectorPending ||
    (showConfigJson && hasJsonError) ||
    hasIncompleteRequiredSchemaConfig ||
    hasInvalidSchemaConfig;

  const isBackDisabled = isCreateConnectorPending;
  const configJsonDisabled = showConfigJson || isCreateConnectorPending;

  const hasConfigFieldValues = schemaFields.some((field) => !!watchedFormConfig?.[field.label]);
  const hasUnmappedConfigValues = Object.keys(jsonUnmappedConfig).length > 0;
  const isConfigReset = hasConfigFieldValues || hasUnmappedConfigValues;

  const resetStep2State = useCallback(() => {
    resetJsonBridge();
    setShowConfigJson(false);
  }, [resetJsonBridge, setShowConfigJson]);

  const handleConfigReset = useCallback(() => {
    resetJsonBridge();
  }, [resetJsonBridge]);

  const resetForm = useCallback(() => {
    form.resetFields();
    resetJsonBridge();
    setShowConfigJson(false);
    setStep(1);
  }, [form, resetJsonBridge, setShowConfigJson]);

  const handleCloseModal = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const handleSelectType = useCallback(() => {
    if (isAssetURIReset) {
      setResetModalAction('typeChange');
    }
  }, [isAssetURIReset]);

  const handleOpenResetModal = useCallback(() => {
    setResetModalAction('resetForm');
  }, []);

  const handleOpenConfigResetModal = useCallback(() => {
    setResetModalAction('resetConfig');
  }, []);

  const handleResetModalConfirm = useCallback(() => {
    handleAssetURIResetFields();
    setResetModalAction(null);
  }, [handleAssetURIResetFields]);

  const handleConfigResetConfirm = useCallback(() => {
    handleConfigReset();
    setResetModalAction(null);
  }, [handleConfigReset]);

  const handleNextStep = useCallback(() => {
    setStep(2);
  }, []);

  const handlePrevStep = useCallback(() => {
    setResetModalAction('prevStep');
  }, []);

  const handlePrevStepConfirm = useCallback(() => {
    resetStep2State();
    setStep(1);
    setResetModalAction(null);
  }, [resetStep2State]);

  const handleResetModalCancel = useCallback(() => {
    setResetModalAction(null);
  }, []);

  const handleSubmit = useCallback(
    async (values: CreateConnectorRequest) => {
      if (!values.type) return;

      const mergedConfig = normalizeConnectorConfigByFields(schemaFields, {
        ...(values.config ?? {}),
        ...jsonUnmappedConfig,
      });

      const request: CreateConnectorRequest = {
        name: values.name,
        type: values.type,
        config: mergedConfig,
        dataInfo: values.dataInfo,
      };

      createConnector(request, {
        onSuccess: () => {
          handleCloseModal();
        },
      });
    },
    [createConnector, handleCloseModal, jsonUnmappedConfig, schemaFields]
  );

  const resetModalConfig = (() => {
    if (resetModalAction === 'typeChange') {
      return {
        description: `Data Source Type을 변경하시겠습니까?\n변경 시 기존 입력값이 모두 초기화됩니다`,
        onConfirm: handleResetModalConfirm,
      };
    }
    if (resetModalAction === 'prevStep') {
      return {
        description: `이전 단계로 이동하시겠습니까?\n이동 시 Config에 입력한 내용이 모두 삭제됩니다`,
        onConfirm: handlePrevStepConfirm,
      };
    }

    if (typeof resetModalAction === 'string' && ['resetForm', 'resetConfig'].includes(resetModalAction)) {
      return {
        description: '기존 입력값이 모두 초기화됩니다',
        onConfirm: resetModalAction === 'resetForm' ? handleResetModalConfirm : handleConfigResetConfirm,
      };
    }

    return null;
  })();

  const footerProps =
    step === 1
      ? {
          cancelText: '취소',
          okText: NEXT_BUTTON_LABEL,
          onClose: handleCloseModal,
          onOk: handleNextStep,
          cancelButtonProps: undefined,
          okButtonProps: { disabled: !hasAssetURIRequiredFields || !name || !selectedType },
          confirmLoading: false,
        }
      : {
          cancelText: PREV_BUTTON_LABEL,
          okText: '생성',
          onClose: handlePrevStep,
          onOk: () => form.submit(),
          cancelButtonProps: { disabled: isBackDisabled },
          okButtonProps: { disabled: !!isSubmitDisabled, loading: isCreateConnectorPending },
          confirmLoading: isCreateConnectorPending,
        };

  useEffect(() => {
    resetJsonBridge();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  return (
    <BaseModal
      width={MODAL_WIDTHS.STANDARD_FORM}
      title="커넥터 생성"
      open={open}
      maskClosable={false}
      onCloseIcon={handleCloseModal}
      onClose={footerProps.onClose}
      onOk={footerProps.onOk}
      okText={footerProps.okText}
      cancelText={footerProps.cancelText}
      confirmLoading={footerProps.confirmLoading}
      cancelButtonProps={footerProps.cancelButtonProps}
      okButtonProps={footerProps.okButtonProps}>
      <Form
        name={CREATE_CONNECTOR_FORM_ID_PREFIX}
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className={styles.form}>
        <ConnectorStep step={step} />

        <ConnectorBasicFields
          connectorTypeOptions={connectorTypeOptions}
          disabled={isCreateConnectorPending || step === 2}
          onSelect={handleSelectType}
        />

        <ResetModal
          open={resetModalAction !== null}
          onCancel={handleResetModalCancel}
          description={resetModalConfig?.description ?? ''}
          onConfirm={resetModalConfig?.onConfirm ?? handleResetModalCancel}
        />

        <ConnectorAssetURIFields step={step} isReset={isAssetURIReset} onResetFields={handleOpenResetModal} />

        {step === 2 && (
          <div className={styles.wrapper}>
            <div className={styles.configHeader}>
              <div className={styles.configTitleWrapper}>
                <h2 className={styles.configTitle}>{CONFIG_FIELDS_TITLE}</h2>
                <Button type="text" onClick={handleOpenConfigResetModal} disabled={!isConfigReset}>
                  <Icon
                    name="reset"
                    size={16}
                    className={classNames(styles.resetIcon, { [styles.resetIconActive]: isConfigReset })}
                  />
                  <span className={classNames(styles.buttonText, { [styles.buttonTextActive]: isConfigReset })}>
                    초기화
                  </span>
                </Button>
              </div>
              <div className={styles.switchWrapper}>
                <label
                  id={`${CREATE_CONNECTOR_FORM_ID_PREFIX}-config-json-switch-label`}
                  htmlFor={`${CREATE_CONNECTOR_FORM_ID_PREFIX}-config-json-switch`}
                  className={styles.switchLabel}>
                  {JSON_VIEW_SWITCH_LABEL}
                </label>
                <Switch
                  id={`${CREATE_CONNECTOR_FORM_ID_PREFIX}-config-json-switch`}
                  checked={showConfigJson}
                  onChange={setShowConfigJson}
                  aria-labelledby={`${CREATE_CONNECTOR_FORM_ID_PREFIX}-config-json-switch-label`}
                  color="standard"
                />
              </div>
            </div>

            <ConnectorConfigFields
              fields={schemaFields}
              isLoading={false}
              isError={isSchemaError}
              disabled={configJsonDisabled}
              showConfigJson={showConfigJson}
            />

            <ConfigJsonView
              key={selectedType}
              idPrefix={CREATE_CONNECTOR_FORM_ID_PREFIX}
              value={jsonDisplayConfig}
              fields={schemaFields}
              onChange={(config, invalidMappedKeys) => {
                if (invalidMappedKeys?.length) {
                  handlePartialConfigChange({ config, invalidMappedKeys });
                  return;
                }

                handleConfigChange(config);
              }}
              onErrorChange={handleJsonErrorChange}
              disabled={isCreateConnectorPending}
              showConfigJson={showConfigJson}
            />
          </div>
        )}
      </Form>
    </BaseModal>
  );
};
